import { Helmet } from "react-helmet";
import { HeadingActionsBar } from "kanopi-components/HeadingActionBar/HeadingActionsBar";
import { Link } from "kanopi-components/Link";
import { VerticalSpacer } from "kanopi-components/SpaceWrapper/VerticalSpacer";
import { TableView } from "kanopi-components/TableView/TableView";
import { ReportsAction } from "kw/admin/ReportsAction";
import { useFormatter } from "kw/util/useFormatter";
import React, { FC, useEffect } from "react";
import { useUser } from "kw/auth/useUser";
import { useReports } from "./hooks/useReports";
import { useStyletron } from "kanopi-components/theme/useStyletron";

export const ReportsPage: FC = () => {
  const { reports, loading, fetchReports, searchReports } = useReports();
  const user = useUser();
  const { formatDate } = useFormatter();
  const [css, theme] = useStyletron();

  const handleSearch = (searchText: string, filterBy: string) => {
    searchReports(searchText, filterBy ? filterBy : "ALL");
  };
  const handlePagination = (search: string, filterBy: string, page: number) => {
    fetchReports(search, filterBy ? filterBy : "ALL", page);
  };
  const handleDownload = (path: string) => {
    const newWindow = window.open(path, "_blank");
    if (newWindow) newWindow.opener = null;
  };
  const handleReportGenerated = () => {
    fetchReports("", "ALL", 1);
  };

  const columnSchema = [
    {
      header: "Report ID",
      id: "reportId",
      children: (row: TReport) => `${row?.id}`,
      overrides: {
        TableHeadCell: {
          style: () => ({
            maxWidth: "10px",
          }),
        },
        TableBodyCell: {
          style: () => ({
            maxWidth: "10px",
          }),
        },
      },
      sortable: false,
    },
    {
      header: "Report name",
      id: "reportName",
      children: (row: TReport) => `${row?.name}`,
      sortable: false,
    },
    {
      header: "Generated date",
      id: "generatedDate",
      children: (row: TReport) => formatDate(row?.generatedDate),
      sortable: false,
    },
    {
      header: "Start date",
      id: "startDatetime",
      children: (row: TReport) => formatDate(row?.startDatetime),
      sortable: false,
    },
    {
      header: "End date",
      id: "endDatetime",
      children: (row: TReport) => formatDate(row?.endDatetime),
      sortable: false,
    },
    {
      header: "",
      id: "download",
      children: (row: TReport) =>
        row?.url ? (
          <Link
            onClick={() => handleDownload(row?.url ?? "")}
            data-testid={`report-${row.id}`}
          >
            Download
          </Link>
        ) : row?.taskStatus === "success" ? (
          <span className={css({ color: theme.colors.contentSecondary })}>
            Generating
          </span>
        ) : (
          <span className={css({ color: theme.colors.negative })}>Error</span>
        ),
      sortable: false,
    },
  ];

  const pageTitle = "Reports";
  const generateTitle = "Generate a report";
  const listingTitle = "Previously generated reports";

  useEffect(() => {
    fetchReports("", "ALL", 1);
  }, []);

  return (
    <>
      <Helmet>
        <title>{`Reports - ${user?.organization?.name}`}</title>
      </Helmet>
      <VerticalSpacer>
        <HeadingActionsBar heading={pageTitle} />

        <ReportsAction
          searchFilters={[
            ...(reports?.reportTypes?.map((report) => ({
              label: report?.name,
              value: report?.type,
            })) || []),
          ]}
          title={generateTitle}
          onReportGenerated={handleReportGenerated}
        />
        <TableView
          title={listingTitle}
          wrapInCard={true}
          columnSchema={columnSchema}
          data={reports?.items || []}
          pageCount={reports?.pageCount || 1}
          onPageChange={handlePagination}
          currentPage={reports?.page || 1}
          loading={loading}
          onSearch={handleSearch}
          searchPlaceholder="Search by report ID"
          type="REPORT"
          showSearchOptions={true}
          searchOptions={[
            { label: "All", value: "ALL" },
            ...(reports?.reportTypes?.map((report) => ({
              label: report?.name,
              value: report?.type,
            })) || []),
          ]}
          filterPlaceholder="Filter by report name"
        />
      </VerticalSpacer>
    </>
  );
};
